import React, { useEffect, useState } from 'react';
import API from '../../Services/api';

// Fixed centre: University of Greenwich
const DEFAULT_COORDS = { longitude: -0.0066, latitude: 51.4831 };

function CollectionPointList() {
  const [collectionPoints, setCollectionPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollectionPoints = async () => {
      setLoading(true);
      try {
        const { data } = await API.get('/collection-points/nearby', {
          params: DEFAULT_COORDS,
        });
        setCollectionPoints(data);
      } catch (err) {
        console.error("Error fetching collection points:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCollectionPoints();
  }, []);

  const typeIcons = {
    'Electronics': '💻',
    'Laptops': '💻',
    'Mobile Phones': '📱',
    'Tablets': '📱',
    'Monitor': '🖥️',
    'Monitors': '🖥️',
    'Batteries': '🔋',
    'Televisions': '📺',
    'Gaming Consoles': '🎮',
    'Printers': '🖨️',
    'Cables & Accessories': '🔌',
    'Small Appliances': '🔧',
    'Washing Machines': '🫧',
    'default': '♻️'
  };

  const getIcon = (name) => typeIcons[name] || typeIcons['default'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#004d3f] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading collection points...</p>
        </div>
      </div>
    );
  }

  if (collectionPoints.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <i className="fas fa-map-marker-alt text-gray-400 text-3xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-1">No collection points found</h3>
        <p className="text-sm text-gray-500 max-w-sm">No recycling centres available in this area yet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {collectionPoints.map((point) => (
        <div key={point._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
          <div className="bg-gradient-to-r from-[#004d3f] to-[#006b57] p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="fas fa-recycle text-white text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white text-base leading-snug">{point.name}</h3>
                <p className="text-green-200 text-xs mt-1">
                  <i className="fas fa-map-marker-alt mr-1"></i>{point.address}
                </p>
              </div>
            </div>
          </div>

          <div className="p-4">
            {point.hours && (
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                <i className="fas fa-clock text-[#004d3f]"></i>
                <span>{point.hours}</span>
              </div>
            )}

            {point.offers && (
              <div className="flex items-center gap-2 mb-3 text-xs text-[#004d3f] bg-green-50 rounded-lg px-3 py-2">
                <i className="fas fa-gift"></i>
                <span>{point.offers}</span>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Accepted Items</p>
              <div className="flex flex-wrap gap-1.5">
                {point.acceptedItems.length > 0 ? point.acceptedItems.map((item, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full">
                    <span>{getIcon(item.name)}</span>
                    <span>{item.name}</span>
                    {item.points > 0 && <span className="text-[#004d3f] font-semibold">+{item.points}pt</span>}
                  </span>
                )) : (
                  <span className="text-xs text-gray-400 italic">All electronics accepted</span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default CollectionPointList;
